import { useDispatch } from 'react-redux';
import { REFRESH_TOKEN_URL } from '@constants';
import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { useAuth } from '@context';
import * as Sentry from '@sentry/react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  getIsTokenExpired,
  getLocalAccessToken,
  getLocalRefreshToken,
  getLocalTokenExpiry,
} from '@utils';
import {
  TRACE_ID_HEADER,
  SESSION_ID_HEADER,
  USER_ID_HEADER,
  BUSINESS_ID_HEADER,
  generateTraceId,
  getOrCreateSessionId,
} from '../observability/traceIds';
import { pushNetworkLog } from '../observability/networkLog';
import { captureFrontendError } from '../observability/capture';

const REQUEST_START_CAP = 256;
const requestStartTimes = new Map<string, number>();

function recordStart(traceId: string) {
  requestStartTimes.set(traceId, Date.now());
  while (requestStartTimes.size > REQUEST_START_CAP) {
    const oldestKey = requestStartTimes.keys().next().value;
    if (oldestKey === undefined) break;
    requestStartTimes.delete(oldestKey);
  }
}

function logResponse(traceId: string | undefined, method: string, url: string, status?: number) {
  if (!traceId) return;
  const startedAt = requestStartTimes.get(traceId);
  const durationMs = startedAt ? Date.now() - startedAt : undefined;
  requestStartTimes.delete(traceId);
  pushNetworkLog({
    traceId,
    at: new Date().toISOString(),
    method,
    url,
    status,
    durationMs,
  });
}
import { RefreshTokenResponse } from './types';
import {
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
  confirmationMessageAtom,
  xHeaderVersionAtom,
  isPublicPageAtom,
} from '../store/jotai';
import {
  addRequestToQueue,
  removeRequestFromQueue,
  setRequestInProgress,
  selectRequestQueue,
  selectIsRequestInProgress,
  getState,
} from '../store/features/auth';

// Public requests

export const http = axios.create({
  baseURL: '/api',
});

http.interceptors.request.use((config) => {
  if (config.headers) {
    const traceId = generateTraceId();
    config.headers[TRACE_ID_HEADER] = traceId;
    config.headers[SESSION_ID_HEADER] = getOrCreateSessionId();
    recordStart(traceId);
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const traceId = response.config.headers?.[TRACE_ID_HEADER] as string | undefined;
    logResponse(
      traceId,
      response.config.method?.toUpperCase() || 'GET',
      response.config.url || '',
      response.status
    );
    return response;
  },
  (error) => {
    const cfg = error?.config;
    const traceId = cfg?.headers?.[TRACE_ID_HEADER] as string | undefined;
    logResponse(
      traceId,
      cfg?.method?.toUpperCase() || 'GET',
      cfg?.url || '',
      error?.response?.status
    );
    return Promise.reject(error);
  }
);

export const useRefreshAuthTokens = () => {
  const { user, setCredentials, logOut } = useAuth();

  return {
    refreshAuthTokens: async () => {
      try {
        const response: RefreshTokenResponse = await http.post(
          REFRESH_TOKEN_URL,
          {
            refreshToken: getLocalRefreshToken(),
            currentToken: getLocalAccessToken(),
            email: user?.email,
          },
          {
            baseURL: '/api',
            headers: {
              'Content-Type': 'application/json',
              withCredentials: true,
            },
          }
        );
        const { accessToken, refreshToken, tokenExpiry, notificationToken } =
          response.data;
        setCredentials(
          { accessToken, refreshToken, tokenExpiry, notificationToken },
          true
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(error);
        if (
          error?.response?.status === 400 ||
          error?.response?.status === 401
        ) {
          logOut();
        }
      }
    },
  };
};

// Authorised requests

type ContentType = 'application/json' | 'multipart/form-data';

export const useAuthHttp = (_contentType: ContentType = 'application/json') => {
  const { user, setCredentials, logOut } = useAuth();
  const { refreshAuthTokens } = useRefreshAuthTokens();
  const setErrorResponseStatusCode = useSetAtom(errorResponseStatusCodeAtom);
  const setResponseErrorMessages = useSetAtom(responseErrorMessagesAtom);
  const setConfirmationMessage = useSetAtom(confirmationMessageAtom);
  const setXHeaderVersion = useSetAtom(xHeaderVersionAtom);
  const isPublicPage = useAtomValue(isPublicPageAtom);
  const dispatch = useDispatch();

  const authHttp = axios.create({
    baseURL: '/api',
    withCredentials: true,
  });

  const processQueue = async () => {
    const state = getState();
    const isRequestInProgress = selectIsRequestInProgress(state);
    const requestQueue = selectRequestQueue(state);

    if (isRequestInProgress || !requestQueue?.length) return;

    dispatch(setRequestInProgress(true));
    const nextRequest = requestQueue[0]; // Get the first request without removing it yet (don't use pop)

    try {
      await nextRequest(); // Execute the request
    } catch (error) {
      console.error('Request failed: ', error);
    } finally {
      dispatch(removeRequestFromQueue());
      dispatch(setRequestInProgress(false));
      processQueue(); // Process the next request if available
    }
  };

  const enqueueRequest = <R = AxiosResponse>(request: () => Promise<R>) => {
    dispatch(addRequestToQueue(request as () => Promise<R>));
    processQueue(); // Start processing immediately if not already in progress
  };

  const executeRequestWithQueue = <T = any, R = AxiosResponse<T>>(
    config: InternalAxiosRequestConfig
  ): Promise<R> => {
    return new Promise((resolve, reject) => {
      enqueueRequest<R>(async () => {
        try {
          const response = (await authHttp(config)) as R; // Execute the request
          resolve(response);
          return response;
        } catch (error) {
          reject(error);
          throw error;
        }
      });
    });
  };

  // REQUEST
  authHttp.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      let token = getLocalAccessToken();
      if (token) {
        const tokenHasExpired = getIsTokenExpired(
          new Date(),
          new Date(getLocalTokenExpiry())
        );

        if (tokenHasExpired) {
          await refreshAuthTokens();
          // update token for the request
          token = getLocalAccessToken();
        }
      }
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
      const traceId = generateTraceId();
      // eslint-disable-next-line no-param-reassign
      config.headers[TRACE_ID_HEADER] = traceId;
      // eslint-disable-next-line no-param-reassign
      config.headers[SESSION_ID_HEADER] = getOrCreateSessionId();
      if (user?.id) {
        // eslint-disable-next-line no-param-reassign
        config.headers[USER_ID_HEADER] = user.id;
      }
      if (user?.businessGroupId) {
        // eslint-disable-next-line no-param-reassign
        config.headers[BUSINESS_ID_HEADER] = user.businessGroupId;
      }
      recordStart(traceId);
      return config;
    },
    // REQUEST ERROR
    async (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
      if (error?.response?.status === 401 && !isPublicPage) {
        logOut();
      }

      return Promise.reject(error);
    }
  );

  // RESPONSE
  authHttp.interceptors.response.use(
    (response) => {
      const traceId = response.config.headers?.[TRACE_ID_HEADER] as string | undefined;
      logResponse(
        traceId,
        response.config.method?.toUpperCase() || 'GET',
        response.config.url || '',
        response.status
      );
      if (response.data.registrationResult) {
        const { accessToken, refreshToken, tokenExpiry, notificationToken } =
          response.data.auth;
        setCredentials(
          { accessToken, refreshToken, tokenExpiry, notificationToken },
          true
        );
      }
      setXHeaderVersion(response.headers['x-version']);
      return response;
    },
    // RESPONSE ERROR
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      // eslint-disable-next-line no-console
      console.error(error);
      if (error?.response?.status === 401 && !isPublicPage) {
        logOut();
      }

      // Check if the status is 400 or 403 and set the jotai state to pop an error toast message
      if (error?.response?.status === 400 || error?.response?.status === 403) {
        setErrorResponseStatusCode(error.response.status);
      }

      if (
        error?.response?.status === 400 &&
        Array.isArray(error?.response?.data?.errors) &&
        error?.response?.data?.errors[0]?.errorMessage
      ) {
        const errorMessages = error.response.data.errors.map(
          (e: { errorMessage: string }) => e.errorMessage
        );
        setResponseErrorMessages(errorMessages);
      } else {
        setResponseErrorMessages([]);
      }
      setConfirmationMessage('');

      setXHeaderVersion(error?.response?.headers['x-version']);

      Sentry.captureException(error);
      const cfg = error?.config;
      const traceId = cfg?.headers?.[TRACE_ID_HEADER] as string | undefined;
      const status = error?.response?.status as number | undefined;
      logResponse(
        traceId,
        cfg?.method?.toUpperCase() || 'GET',
        cfg?.url || '',
        status
      );
      if (!status || status >= 500) {
        captureFrontendError(error, {
          traceId,
          route: cfg?.url,
          method: cfg?.method?.toUpperCase(),
          statusCode: status,
        });
      }

      return Promise.reject(error);

      // const originalRequest = error.config;

      // // If the error is a 401 and we have a refresh token, refresh the JWT token
      // if (
      //   error.response.status === 401 &&
      //   localStorage.getItem('refreshToken')
      // ) {
      //   refreshAuthTokens()
      //     .then((response) => {
      //       const token = getLocalAccessToken();
      //       // Re-run the original request that was intercepted
      //       originalRequest.headers.Authorization = `Bearer ${token}`;
      //       authHttp(originalRequest)
      //         .then((res) => {
      //           return res.data;
      //         })
      //         .catch((err) => {
      //           console.log(err);
      //         });
      //     })
      //     .catch((err) => {
      //       // If there is an error refreshing the token, log out the user
      //       console.log(err);
      //     });
      // }

      // // Return the original error if we can't handle it
      // return Promise.reject(error);
    }
  );

  const defaultAxiosInstance = axios.create();

  defaultAxiosInstance.get = <T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> =>
    executeRequestWithQueue({
      ...config,
      method: 'get',
      url,
      headers:
        (config?.headers as AxiosHeaders) ||
        defaultAxiosInstance.defaults.headers,
    });

  defaultAxiosInstance.post = <T = any, R = AxiosResponse<T>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R> =>
    executeRequestWithQueue({
      ...config,
      method: 'post',
      url,
      data,
      headers:
        (config?.headers as AxiosHeaders) ||
        defaultAxiosInstance.defaults.headers,
    });

  return defaultAxiosInstance;
};
